import { RefObject, useCallback, useEffect, useState } from 'react'
import Mark from 'mark.js'

type Match = { page: number; elementIndex: number }

type UsePdfSearchReturn = {
    query: string
    lastQuery: string
    matches: Match[]
    activeIndex: number
}

type UsePdfSearchProps = {
    pageRefs: RefObject<(HTMLDivElement | null)[]>
    setActivePage: (page: number) => void
    setJumpPageInput: (val: string) => void
}

export function usePdfSearch({ pageRefs, setActivePage, setJumpPageInput }: UsePdfSearchProps) {
    const [searchState, setSearchState] = useState<UsePdfSearchReturn>({
        query: '',
        lastQuery: '',
        matches: [],
        activeIndex: 0,
    })

    const resetHighlights = useCallback(() => {
        pageRefs.current.forEach((pageRef) => {
            if (!pageRef) {
                return
            }
            const markInstance = new Mark(pageRef)
            markInstance.unmark()
        })
    }, [pageRefs])

    const finalizeSearchResults = (matches: Match[], query: string) => {
        if (matches.length === 0) {
            setSearchState((prev) => ({
                ...prev,
                matches: [],
                activeIndex: 0,
                lastQuery: query,
            }))
            return
        }

        const sortedMatches = matches.slice().sort((a, b) => {
            if (a.page !== b.page) {
                return a.page - b.page
            }
            return a.elementIndex - b.elementIndex
        })

        pageRefs.current.forEach((ref) => {
            ref?.querySelectorAll('mark.pdf-highlight').forEach((el) => {
                el.classList.remove('active')
            })
        })

        const first = sortedMatches[0]
        const pageRef = pageRefs.current[first.page - 1]
        const highlightElements = pageRef?.querySelectorAll('mark.pdf-highlight')
        const element = highlightElements?.[first.elementIndex]

        if (element) {
            element.classList.add('active')
            element.scrollIntoView({ block: 'center' })
        }

        setActivePage(first.page)
        setJumpPageInput(String(first.page))

        setSearchState((prev) => ({
            ...prev,
            matches: sortedMatches,
            activeIndex: 0,
            lastQuery: query,
        }))
    }

    const highlightPage = (pageRef: HTMLDivElement, pageIndex: number, query: string, matches: Match[]) => {
        const markInstance = new Mark(pageRef)
        markInstance.unmark({
            done: () => {
                markInstance.mark(query, {
                    separateWordSearch: false,
                    className: 'pdf-highlight',
                    done: () => {
                        const highlights = pageRef.querySelectorAll('mark.pdf-highlight')
                        highlights.forEach((_, i) => {
                            matches.push({ page: pageIndex + 1, elementIndex: i })
                        })

                        if (pageIndex === pageRefs.current.length - 1) {
                            finalizeSearchResults(matches, query)
                        }
                    },
                })
            },
        })
    }

    const scrollToMatch = (match: Match, index: number) => {
        pageRefs.current.forEach((ref) => {
            ref?.querySelectorAll('mark.pdf-highlight').forEach((el) => {
                el.classList.remove('active')
            })
        })

        const pageRef = pageRefs.current[match.page - 1]
        const highlightElements = pageRef?.querySelectorAll('mark.pdf-highlight')
        const element = highlightElements?.[match.elementIndex]

        if (element) {
            element.classList.add('active')
            element.scrollIntoView({ block: 'center' })
        }

        setActivePage(match.page)
        setJumpPageInput(String(match.page))
        setSearchState((prev) => ({
            ...prev,
            activeIndex: index,
        }))
    }

    const performSearch = () => {
        const query = searchState.query.trim()
        if (!query) {
            resetHighlights()
            setSearchState({
                query: '',
                lastQuery: '',
                matches: [],
                activeIndex: 0,
            })
            return
        }

        if (query !== searchState.lastQuery) {
            const matches: Match[] = []

            pageRefs.current.forEach((pageRef, pageIndex) => {
                if (pageRef) {
                    highlightPage(pageRef, pageIndex, query, matches)
                }
            })

            return
        }

        if (searchState.matches.length === 0) {
            return
        }

        const nextIndex = (searchState.activeIndex + 1) % searchState.matches.length
        const next = searchState.matches[nextIndex]
        scrollToMatch(next, nextIndex)
    }

    const navigateToMatch = (index: number) => {
        const match = searchState.matches[index]
        scrollToMatch(match, index)
    }

    const resetSearch = () => {
        resetHighlights()
        setSearchState({
            query: '',
            lastQuery: '',
            matches: [],
            activeIndex: 0,
        })
    }

    const setSearchQuery = (query: string) => {
        setSearchState((prev) => ({
            ...prev,
            query: query.trim(),
        }))
    }

    useEffect(() => {
        return () => {
            resetHighlights()
        }
    }, [resetHighlights])

    return {
        searchQuery: searchState.query,
        performSearch,
        searchMatches: searchState.matches,
        activeMatchIndex: searchState.activeIndex,
        resetSearch,
        setSearchQuery,
        navigateToMatch,
    }
}
